import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { repeatOf, byTime, todayISO, formatDate } from "@/lib/schedule";
import { useLang } from "@/lib/i18n";
import { Clock, MapPin, Loader2, CalendarDays, Sun } from "lucide-react";

function ScheduleRow({ s }) {
  const { t, tr } = useLang();
  return (
    <div className="flex gap-5 bg-white rounded-xl border border-[#E2DAD0] p-5 hover:border-[#C59B27] transition-colors" data-testid={`public-schedule-${s.id}`}>
      <div className="w-24 shrink-0 text-center pt-1">
        <div className="font-mono text-[#C59B27] font-semibold text-sm">{s.time}</div>
      </div>
      <div className="flex-1 border-l border-[#E2DAD0] pl-5">
        <div className="font-semibold text-[#194324] text-lg">{tr(s, "program")}</div>
        {s.location && <div className="text-sm text-[#5C7264] flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" />{t(s.location)}</div>}
        {s.description && <p className="text-sm text-[#3A4E41] mt-2">{tr(s, "description")}</p>}
      </div>
    </div>
  );
}

function Group({ icon: Icon, title, rows, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-[#C59B27]" />
        <h2 className="font-display text-2xl font-semibold text-[#194324]">{title}</h2>
      </div>
      <div className="space-y-3">{rows.map((s) => <ScheduleRow key={s.id} s={s} />)}</div>
    </div>
  );
}

export default function SchedulePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/public/schedules").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const daily = items.filter((s) => repeatOf(s) === "daily").sort(byTime);
  const today = todayISO();
  const special = items.filter((s) => repeatOf(s) === "once" && s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || byTime(a, b))
    .reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const specialDates = Object.keys(special);
  const empty = daily.length === 0 && specialDates.length === 0;

  return (
    <div data-testid="public-schedule-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Daily Rhythm")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Ashram Schedule")}</h1>
          <p className="text-white/70 mt-3 max-w-xl">{t("From Brahma Muhurta meditation to Sandhya Aarti — the daily flow of sadhana and seva at our ashram.")}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : empty ? (
          <div className="text-center py-16 text-[#5C7264]"><Clock className="h-12 w-12 mx-auto mb-4 text-[#CBD2CC]" /><p>{t("No schedule published yet.")}</p></div>
        ) : (
          <div className="space-y-12">
            {daily.length > 0 && <Group icon={Sun} title={t("Daily Schedule")} rows={daily} testid="public-schedule-daily" />}
            {specialDates.length > 0 && (
              <div className="space-y-10" data-testid="public-schedule-special">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("Special Programmes")}</p>
                {specialDates.map((d) => <Group key={d} icon={CalendarDays} title={formatDate(d, lang)} rows={special[d]} testid={`public-schedule-date-${d}`} />)}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
